import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Form, Table, Button, Pagination } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiEye,
  FiAlertTriangle,
  FiBellOff,
  FiRotateCcw,
  FiRefreshCw,
  FiPackage,
} from "react-icons/fi";
import Swal from "sweetalert2";
import Select from "react-select";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const PAGE_WINDOW = 5;

const lastPageOf = (total, perPage) => Math.max(1, Math.ceil(total / perPage));

const TablePagination = ({ t, page, perPage, total, onPageChange }) => {
  if (total === 0) return null;

  const lastPage = lastPageOf(total, perPage);
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  let start = Math.max(1, page - Math.floor(PAGE_WINDOW / 2));
  const end = Math.min(lastPage, start + PAGE_WINDOW - 1);
  start = Math.max(1, end - PAGE_WINDOW + 1);

  const pages = [];
  for (let p = start; p <= end; p += 1) pages.push(p);

  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 border-top">
      <span className="text-muted small">
        {t("showing")} <span className="fw-bold">{from}</span> - <span className="fw-bold">{to}</span>{" "}
        {t("of")} <span className="fw-bold">{total}</span>
      </span>

      {lastPage > 1 && (
        <Pagination size="sm" className="mb-0">
          <Pagination.First disabled={page === 1} onClick={() => onPageChange(1)} />
          <Pagination.Prev disabled={page === 1} onClick={() => onPageChange(page - 1)} />

          {start > 1 && <Pagination.Ellipsis disabled />}

          {pages.map((p) => (
            <Pagination.Item key={p} active={p === page} onClick={() => onPageChange(p)}>
              {p}
            </Pagination.Item>
          ))}

          {end < lastPage && <Pagination.Ellipsis disabled />}

          <Pagination.Next disabled={page === lastPage} onClick={() => onPageChange(page + 1)} />
          <Pagination.Last disabled={page === lastPage} onClick={() => onPageChange(lastPage)} />
        </Pagination>
      )}
    </div>
  );
};

export default function LowStockIndex() {
  const { t } = useContext(TranslatorContext);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [showDismissed, setShowDismissed] = useState(true);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [dismissedPage, setDismissedPage] = useState(1);

  useEffect(() => {
    fetchLowStock();
  }, []);

  // A new filter or page size always starts from the first page.
  useEffect(() => {
    setPage(1);
    setDismissedPage(1);
  }, [searchTerm, selectedCategory, selectedVendor, perPage]);

  const notify = (icon, title, text) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      text,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const fetchLowStock = () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    axios
      .get(`${process.env.REACT_APP_API_URL}/stock/low-stock`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.status) {
          setData(res.data.stock || []);
          setDismissed(res.data.dismissed || []);
          setCategories(res.data.categories || []);
          setVendors(res.data.vendor || []);
          setThreshold(res.data.low_stock_threshold ?? 3);
        } else {
          setData([]);
          setDismissed([]);
        }
      })
      .catch((err) => console.error("Error fetching low stock products:", err))
      .finally(() => setLoading(false));
  };

  const toggleDismissal = async (product, dismiss) => {
    if (dismiss) {
      const confirmed = await Swal.fire({
        title: t("Dismiss low stock alert?"),
        html: `<strong>${product.name}</strong> ${t(
          "will be hidden from the low stock list until it is restocked."
        )}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("Yes, dismiss"),
        cancelButtonText: t("Cancel"),
        confirmButtonColor: "#d33",
      });

      if (!confirmed.isConfirmed) return;
    }

    const token = localStorage.getItem("authToken");
    const endpoint = dismiss ? "dismiss-low-stock" : "restore-low-stock";

    setUpdatingId(product.id);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/stock/${product.id}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status) {
        if (dismiss) {
          setData((prev) => prev.filter((item) => item.id !== product.id));
          setDismissed((prev) => [
            ...prev,
            { ...product, low_stock_dismissed: true },
          ]);
        } else {
          setDismissed((prev) => prev.filter((item) => item.id !== product.id));
          setData((prev) =>
            [...prev, { ...product, low_stock_dismissed: false }].sort(
              (a, b) => a.available_stock - b.available_stock
            )
          );
        }

        notify(
          "success",
          dismiss ? t("Dismissed") : t("Restored"),
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error updating low stock alert:", error);
      notify(
        "error",
        t("Update Failed"),
        error.response?.data?.message || t("Failed to update the low stock alert")
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const vendorOptions = vendors.map((v) => ({
    value: v.id,
    label: v.company_name,
  }));

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const applyFilters = (rows) =>
    rows.filter((item) => {
      const matchesSearch = item.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory
        ? item.category_id === selectedCategory
        : true;
      const matchesVendor = selectedVendor
        ? item.vendor_id === selectedVendor
        : true;

      return matchesSearch && matchesCategory && matchesVendor;
    });

  const filteredData = applyFilters(data);
  const filteredDismissed = applyFilters(dismissed);

  // Dismissing the last row of a page would otherwise leave it empty.
  const currentPage = Math.min(page, lastPageOf(filteredData.length, perPage));
  const currentDismissedPage = Math.min(
    dismissedPage,
    lastPageOf(filteredDismissed.length, perPage)
  );

  const pageOffset = (currentPage - 1) * perPage;
  const dismissedOffset = (currentDismissedPage - 1) * perPage;

  const pagedData = filteredData.slice(pageOffset, pageOffset + perPage);
  const pagedDismissed = filteredDismissed.slice(
    dismissedOffset,
    dismissedOffset + perPage
  );

  const outOfStockCount = data.filter((item) => item.available_stock <= 0).length;

  const formatPrice = (value) =>
    value === null || value === undefined || value === ""
      ? "-"
      : parseFloat(value).toLocaleString();

  const renderRows = (rows, isDismissedList, offset = 0) =>
    rows.map((item, index) => (
      <tr key={item.id}>
        <td className="text-center">{offset + index + 1}</td>

        <td>
          <strong>{item.name}</strong>
          <div className="text-muted small">{item.vendor || "-"}</div>
        </td>

        <td>
          <span className="badge bg-light text-dark">{item.category_name}</span>
        </td>

        <td>
          {item.purchase_prices?.length
            ? item.purchase_prices.map((p, idx) => (
              <div key={idx}>
                {p.purchase_price ? formatPrice(p.purchase_price) : t("Free")}
              </div>
            ))
            : formatPrice(item.purchase_price)}
        </td>

        <td>{formatPrice(item.sale_price)}</td>

        <td className="text-center">
          <span
            className={`badge px-3 py-2 ${item.available_stock <= 0 ? "bg-dark" : "bg-danger"
              }`}
          >
            {item.available_stock}
          </span>
        </td>


        <td className="text-center">
          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="link"
              className="p-0 text-primary"
              onClick={() => navigate(`/stock/show/${item.id}`)}
              title={t("View Sales History")}
            >
              <FiEye size={17} />
            </Button>

            {isDismissedList ? (
              <Button
                size="sm"
                variant="primary"
                className="d-inline-flex align-items-center gap-1"
                disabled={updatingId === item.id}
                onClick={() => toggleDismissal(item, false)}
                title={t("Restore to low stock list")}
              >
                <FiRotateCcw size={13} />
                {t("Restore")}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                className="d-inline-flex align-items-center gap-1"
                disabled={updatingId === item.id}
                onClick={() => toggleDismissal(item, true)}
                title={t("Dismiss from low stock list")}
              >
                <FiBellOff size={13} />
                {t("Dismiss")}
              </Button>
            )}
          </div>
        </td>
      </tr>
    ));

  if (loading) {
    return (
      <PageLayout>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("loading")}...</span>
          </div>
          <p className="mt-3">{t("loading")}...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb p-3">
              <h3 className="mc-breadcrumb-title mb-0">
                {t("Low Stock Products")}
              </h3>
              <ul className="mc-breadcrumb-list mb-0">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">
                    {t("home")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="/stock" className="mc-breadcrumb-link">
                    {t("stock management")}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{t("Low Stock Products")}</li>
              </ul>
            </div>
          </div>
        </Col>

        <Col xl={4} md={4} className="mb-3">
          <div className="mc-card p-3 d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: 46, height: 46, background: "rgba(220,53,69,0.12)" }}
            >
              <FiAlertTriangle size={20} color="#dc3545" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">
                {t("Low Stock Products")}
              </div>
              <h4 className="mb-0">{data.length}</h4>
            </div>
          </div>
        </Col>

        <Col xl={4} md={4} className="mb-3">
          <div className="mc-card p-3 d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: 46, height: 46, background: "rgba(33,37,41,0.1)" }}
            >
              <FiPackage size={20} color="#212529" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">
                {t("Out Of Stock")}
              </div>
              <h4 className="mb-0">{outOfStockCount}</h4>
            </div>
          </div>
        </Col>

        <Col xl={4} md={4} className="mb-3">
          <div className="mc-card p-3 d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded"
              style={{ width: 46, height: 46, background: "rgba(108,117,125,0.12)" }}
            >
              <FiBellOff size={20} color="#6c757d" />
            </div>
            <div>
              <div className="text-muted small text-uppercase">
                {t("Dismissed")}
              </div>
              <h4 className="mb-0">{dismissed.length}</h4>
            </div>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h5 className="mb-0">{t("Low Stock List")}</h5>
                <span className="badge bg-danger">{filteredData.length}</span>
                <span className="text-muted small">
                  {t("Low stock at")} {threshold} {t("or less")}
                </span>
              </div>

              <div className="d-flex gap-3 flex-wrap">
                <Select
                  value={
                    categoryOptions.find((opt) => opt.value === selectedCategory) ||
                    null
                  }
                  onChange={(option) =>
                    setSelectedCategory(option ? option.value : "")
                  }
                  options={categoryOptions}
                  isClearable
                  isSearchable
                  placeholder={t("All categories")}
                  styles={{ container: (base) => ({ ...base, width: 200 }) }}
                />

                <Select
                  value={
                    vendorOptions.find((opt) => opt.value === selectedVendor) || null
                  }
                  onChange={(option) =>
                    setSelectedVendor(option ? option.value : "")
                  }
                  options={vendorOptions}
                  isClearable
                  isSearchable
                  placeholder={t("All Vendor")}
                  styles={{ container: (base) => ({ ...base, width: 200 }) }}
                />

                <div className="position-relative">
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={t("search products...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingRight: "35px", width: "250px" }}
                  />
                  <FiSearch
                    className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                    size={16}
                  />
                </div>

                <Form.Select
                  size="sm"
                  value={perPage}
                  onChange={(e) => setPerPage(Number(e.target.value))}
                  style={{ width: 110 }}
                  aria-label={t("Rows per page")}
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / {t("page")}
                    </option>
                  ))}
                </Form.Select>

                <Button
                  size="sm"
                  variant="secondary"
                  className="d-inline-flex align-items-center gap-1"
                  onClick={fetchLowStock}
                >
                  <FiRefreshCw size={14} />
                  {t("Refresh")}
                </Button>
              </div>
            </div>

            <div className="table-responsive-wrapper">
              <Table responsive hover className="table mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "5%" }}>
                      #
                    </th>
                    <th style={{ width: "22%" }}>{t("product name")}</th>
                    <th style={{ width: "13%" }}>{t("category")}</th>
                    <th style={{ width: "17%" }}>{t("Purchase Price")}</th>
                    <th style={{ width: "12%" }}>{t("Sale Price")}</th>
                    <th className="text-center" style={{ width: "10%" }}>
                      {t("available stock")}
                    </th>

                    <th className="text-center" style={{ width: "13%" }}>
                      {t("actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.length > 0 ? (
                    renderRows(pagedData, false, pageOffset)
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <div className="empty-state">
                          <FiAlertTriangle size={48} className="text-muted mb-2" />
                          <p className="text-muted mb-0">
                            {t("No low stock products found")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <TablePagination
              t={t}
              page={currentPage}
              perPage={perPage}
              total={filteredData.length}
              onPageChange={setPage}
            />
          </div>
        </Col>

        {dismissed.length > 0 && (
          <Col xl={12} className="mt-3">
            <div className="mc-card">
              <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-3">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="mb-0">{t("Dismissed Products")}</h5>
                  <span className="badge bg-secondary">{dismissed.length}</span>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowDismissed((prev) => !prev)}
                >
                  {showDismissed ? t("Hide") : t("Show")}
                </Button>
              </div>

              {showDismissed && (
                <div className="table-responsive-wrapper">
                  <Table responsive hover className="table mb-0">
                    <thead>
                      <tr>
                        <th className="text-center" style={{ width: "5%" }}>
                          #
                        </th>
                        <th style={{ width: "22%" }}>{t("product name")}</th>
                        <th style={{ width: "13%" }}>{t("category")}</th>
                        <th style={{ width: "17%" }}>{t("Purchase Price")}</th>
                        <th style={{ width: "12%" }}>{t("Sale Price")}</th>
                        <th className="text-center" style={{ width: "10%" }}>
                          {t("available stock")}
                        </th>
                        <th className="text-center" style={{ width: "13%" }}>
                          {t("actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedDismissed.length > 0 ? (
                        renderRows(pagedDismissed, true, dismissedOffset)
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <p className="text-muted mb-0">
                              {t("No dismissed products found")}
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {showDismissed && (
                <TablePagination
                  t={t}
                  page={currentDismissedPage}
                  perPage={perPage}
                  total={filteredDismissed.length}
                  onPageChange={setDismissedPage}
                />
              )}
            </div>
          </Col>
        )}
      </Row>
    </PageLayout>
  );
}
